import {computed, type Ref} from 'vue';
import {useRoute} from '@typed-router';
import {storeToRefs} from 'pinia';
import {useAuth, useDataTracking, useLazyAsyncData, useLoadable} from '#imports';
import type {FetchError, Origin, ProposalInformationResponse, SubmittedBriefResponse} from '@api';
import {useProposalStore} from './proposal.store';
import type {SubmitProposalFormState} from './types';

export function useInitProposalStore() {
    const route = useRoute();
    const auth = useAuth();
    const {track} = useDataTracking();

    const resumeBriefId = route.query.resumeBriefId?.toString();
    const profileIds = route.query.profileIds ? (route.query.profileIds.toString().split(',') as string[]) : undefined;
    const searchId = route.query.searchId?.toString();
    const profileViewId = route.query.profileViewId?.toString();
    const origin = route.query.origin?.toString() as Origin;
    const entryPoint = route.query.entryPoint?.toString();

    const store = useProposalStore();
    const {proposalInformation} = storeToRefs(store);

    const {pending, error} = useLazyAsyncData<void, FetchError>(
        'init_proposal_store',
        async () => {
            await store.init(resumeBriefId, profileIds, searchId, profileViewId, origin, auth.loggedIn && auth.currentIdentityIsClient);

            track('search_brief_started', {
                search_event_id: searchId,
                shortlisted_freelancers_number: proposalInformation.value?.profiles.length,
                entry_point: entryPoint,
            });
        },
        {deep: true},
    );

    return {pending, error, proposalInformation};
}

export function useRefreshProposalStore() {
    const store = useProposalStore();
    const {proposalInformation} = storeToRefs(store);

    const {error, execute: refresh} = useLazyAsyncData<void, FetchError>('refresh_proposal_store', () => store.refresh(), {
        immediate: false,
        deep: true,
    });

    return {error, refresh, proposalInformation};
}

export function useProposalInformation() {
    const store = useProposalStore();
    const {proposalInformation} = storeToRefs(store);

    if (!proposalInformation.value) {
        throw new Error('Unable to load proposal information, make sure the store is initialized');
    }

    return {proposalInformation: proposalInformation as Ref<ProposalInformationResponse>};
}

export function useSubmittedBriefs() {
    const store = useProposalStore();
    const {submittedBriefs, canLoadMoreSubmittedBriefs} = storeToRefs(store);

    const {
        execute: loadMoreSubmittedBriefs,
        status,
        error,
    } = useLazyAsyncData<void, FetchError>('load_more_submitted_briefs', () => store.loadMoreSubmittedProposals(), {immediate: false, deep: true});

    const loadingMore = computed(() => status.value === 'pending');

    return {submittedBriefs, canLoadMoreSubmittedBriefs, loadMoreSubmittedBriefs, loadingMore, error};
}

export function useSelectedBrief() {
    const store = useProposalStore();
    const {selectedBrief} = storeToRefs(store);

    function selectBrief(brief: SubmittedBriefResponse) {
        selectedBrief.value = brief;
    }

    return {selectedBrief, selectBrief};
}

export function useAddFreelancersToCurrentBrief() {
    const store = useProposalStore();

    const {execute: addFreelancersToCurrentBrief, loading} = useLoadable((description?: string) => store.addFreelancersToCurrentBrief(description));

    return {addFreelancersToCurrentBrief, loading};
}

export function useSubmitProposal() {
    const store = useProposalStore();

    const {execute, loading} = useLoadable((proposal: SubmitProposalFormState) => store.submitProposal(proposal));

    return {submitProposal: execute, loading};
}
